import type { MouseEventHandler } from 'react';
import React from 'react';
import { styled, css } from 'styled-components';

import { Media } from '../../theme/Media.tsx';
import { Tooltip } from '../../V2/Feedback/Tooltip/index.tsx';
import { useTableContext, type TableRowIndex } from './context.tsx';
import { Wrapper as TdWrapper, Container as TdContainer } from './Td.tsx';

export interface IWrapper {
  onClick?: () => void;
  $isActive: boolean;
  $isInactive: boolean;
  $enabled?: boolean;
  $bgColor?: string;
  $isCollapsible?: boolean;
  $isUnCollapsed?: boolean;
  $isCollapsedNav?: boolean;
  /**
   * If `true` will
   */
  $hover?: boolean;
}

export const Wrapper = styled.tr<IWrapper>`
  position: relative;
  cursor: ${({ onClick }) => !!onClick && 'pointer'};
  color: ${({ $isInactive, theme }) => $isInactive && theme.colors.grey70_1};
  ${({ $isUnCollapsed, theme }) =>
    $isUnCollapsed &&
    css`
      ${TdWrapper} {
        background: ${theme.colors.grey30_1};
      }
    `}
  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      ${TdWrapper} {
        background: ${theme.colors.grey30_1};
      }
    `}
  ${({ $bgColor, $isActive }) =>
    !!$bgColor &&
    !$isActive &&
    css`
      ${TdWrapper} {
        background: ${$bgColor};
      }
    `}
  ${({ $enabled, theme }) =>
    $enabled &&
    css`
      ${TdWrapper} {
        &:first-child {
          ${TdContainer} {
            position: relative;
            &:before {
              content: '';
              display: block;
              height: 100%;
              width: 2px;
              position: absolute;
              left: 0;
              top: 0;
              background: ${theme.colors.main};
            }
          }
        }
      }
    `}
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    ${({ $hover, theme }) =>
      $hover &&
      css`
        &:hover ${TdWrapper} {
          background-color: ${theme.colors.grey05_1};
        }
      `}

    ${({ $isCollapsible, theme }) =>
      $isCollapsible &&
      css`
        &:hover ${TdWrapper} {
          background-color: ${theme.colors.grey30_1};
        }
      `}
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 2px;
  }

  @media ${Media.mdMin} {
    @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
      width: calc(50% - 2px);
      &:nth-child(even) {
        margin-left: 4px;
      }
    }
  }
  @media ${Media.smMax} {
    width: 100%;
  }
`;

export interface ITr {
  onClick?: MouseEventHandler<HTMLTableRowElement>;
  onMouseLeave?: MouseEventHandler<HTMLTableRowElement>;
  children?: React.ReactNode;
  className?: string;
  rowIndex?: TableRowIndex;
  enabled?: boolean;
  bgColor?: string;
  dataTestId?: string;
  isCollapsible?: boolean;
  isUnCollapsed?: boolean;
  tooltip?: string;
  isInactive?: boolean;
  /**
   * If enabled, hover styles will be applied to the row under the cursor.
   */
  hover?: boolean;
}

const Tr = React.forwardRef<HTMLTableRowElement, ITr & { ref?: React.Ref<HTMLTableRowElement> }>(function Tr(
  {
    onClick,
    children,
    className,
    rowIndex,
    enabled,
    bgColor,
    dataTestId,
    onMouseLeave,
    isCollapsible,
    isUnCollapsed,
    tooltip,
    hover,
    isInactive,
  },
  ref,
) {
  const { activeRow, isCollapsedNav } = useTableContext();
  return (
    <Tooltip
      tooltip={tooltip}
      placement='bottom'
      className={className}
      dataTestId={dataTestId}
      ContainerComponent={Wrapper}
      containerComponentProps={{
        $enabled: enabled,
        $isActive: activeRow !== undefined && rowIndex !== undefined && rowIndex === activeRow,
        $isInactive: isInactive,
        $bgColor: bgColor,
        onClick,
        ref,
        onMouseLeave,
        $isCollapsible: isCollapsible,
        $isUnCollapsed: isUnCollapsed,
        $isCollapsedNav: isCollapsedNav,
        $hover: hover,
      }}>
      {children}
    </Tooltip>
  );
});

export default React.memo(Tr);
